/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import './LocationFilters.css';

const ALL = 0;
const SELECT_LOCATION = -1;

// Location Types enum matching the C# enum
const LocationType = {
    Make: 1,
    Region: 2,
    District: 3
};

const LocationFilters = ({
    locationFilters,
    onSearchButtonClicked,
    pageText,
    preselectFilters,
}) => {
    const { hasGlobalScope } = locationFilters || false;

    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedMakePK, setSelectedMakePK] = useState(SELECT_LOCATION);
    const [selectedRegionPK, setSelectedRegionPK] = useState(SELECT_LOCATION);
    const [selectedDistrictPK, setSelectedDistrictPK] = useState(SELECT_LOCATION);
    const [renderResetKey, setRenderResetKey] = useState(0);

    const makes = useMemo(() => {
        return [...(locationFilters?.makes ?? [])].sort((a, b) => a.title.localeCompare(b.title));
    }, [locationFilters?.makes]);

    // Memoized, sorted lists reused across handlers/effects
    const sortedRegions = useMemo(() => {
        return [...(locationFilters?.regions ?? [])].sort((a, b) => a.description.localeCompare(b.description));
    }, [locationFilters?.regions]);

    const sortedDistricts = useMemo(() => {
        return [...(locationFilters?.districts ?? [])].sort((a, b) => a.description.localeCompare(b.description));
    }, [locationFilters?.districts]);

    const assignedMakePKs = useMemo(() => {
        return new Set(locationFilters?.assignedMakePKs ?? []);
    }, [locationFilters?.assignedMakePKs]);

    const assignedRegionPKs = useMemo(() => {
        return new Set(locationFilters?.assignedRegionPKs ?? []);
    }, [locationFilters?.assignedRegionPKs]);

    // Force remount of selects after history (back/forward) restore so DOM matches state
    useEffect(() => {
        const handlePageShow = (evt) => {
            setRenderResetKey(k => k + 1);
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    useEffect(() => {
        if (!preselectFilters) {
            return;
        }

        let { makePK, regionPK, districtPK } = preselectFilters;

        makePK = parseInt(makePK);
        regionPK = parseInt(regionPK);
        districtPK = parseInt(districtPK);

        // Build regions list (clone + sort; no mutation)
        let regionsFilteredByMake = sortedRegions.filter(x => x.makeFK === makePK);

        const showAllRegions = hasGlobalScope || assignedMakePKs.has(makePK);
        if (showAllRegions) {
            regionsFilteredByMake.unshift({
                regionPK: ALL,
                makeFK: makePK,
                description: pageText.allRegions,
            });
        }

        // Build districts list (clone + sort; no mutation)
        let districtsFilteredByRegion = sortedDistricts.filter(x => x.regionFK === regionPK);

        const showAllDistricts = hasGlobalScope || assignedRegionPKs.has(regionPK) || assignedMakePKs.has(makePK);
        if (showAllDistricts) {
            districtsFilteredByRegion.unshift({
                districtPK: ALL,
                regionFK: regionPK,
                makeFK: makePK,
                description: pageText.allDistricts,
            });
        }

        setRegions(regionsFilteredByMake);
        setDistricts(districtsFilteredByRegion);
        setSelectedMakePK(makePK);
        setSelectedRegionPK(regionPK);
        setSelectedDistrictPK(districtPK);
    }, [preselectFilters, sortedRegions, sortedDistricts, hasGlobalScope, assignedMakePKs, assignedRegionPKs, pageText.allRegions, pageText.allDistricts]);

    const handleMakeSelected = (rawMakePK) => {
        const makePK = parseInt(rawMakePK);

        let regionsFilteredByMake = sortedRegions.filter(x => x.makeFK === makePK);

        // show 'All Regions' option in Region dropdown if user has been assigned global scope or entire parent make
        const showAllRegions = hasGlobalScope || assignedMakePKs.has(makePK);
        if (showAllRegions) {
            regionsFilteredByMake.unshift({
                regionPK: ALL,
                makeFK: makePK,
                description: pageText.allRegions,
            });
        }

        setSelectedMakePK(makePK);
        setSelectedRegionPK(SELECT_LOCATION);
        setSelectedDistrictPK(SELECT_LOCATION);
        setRegions(regionsFilteredByMake);
        setDistricts([]);
    };

    const handleRegionSelected = (rawRegionPK) => {
        const regionPK = parseInt(rawRegionPK);

        let districtsFilteredByRegion = sortedDistricts.filter(x => x.regionFK === regionPK);

        const parentMakeFK = districtsFilteredByRegion.length > 0
            ? districtsFilteredByRegion[0].makeFK
            : selectedMakePK;

        // show 'All Districts' option in District dropdown only if user has been assigned global scope or entire parent region or entire parent make
        const showAllDistricts = hasGlobalScope || assignedRegionPKs.has(regionPK) || assignedMakePKs.has(parentMakeFK);
        if (showAllDistricts) {
            districtsFilteredByRegion.unshift({
                districtPK: ALL,
                regionFK: regionPK,
                makeFK: parentMakeFK,
                description: pageText.allDistricts,
            });
        }

        setSelectedRegionPK(regionPK);
        setSelectedDistrictPK(regionPK === ALL ? ALL : SELECT_LOCATION);
        setDistricts(districtsFilteredByRegion);
    };

    const handleDistrictSelected = (rawDistrictPK) => {
        const districtPK = parseInt(rawDistrictPK);
        setSelectedDistrictPK(districtPK);
    };

    const getSelectedLocation = () => {
        if (selectedDistrictPK !== ALL && selectedDistrictPK !== SELECT_LOCATION) {
            return {
                makePK: selectedMakePK,
                regionPK: selectedRegionPK,
                districtPK: selectedDistrictPK,
                locationType: LocationType.District,
                locationPK: selectedDistrictPK
            };
        } else if (selectedRegionPK !== ALL && selectedRegionPK !== SELECT_LOCATION) {
            return {
                makePK: selectedMakePK,
                regionPK: selectedRegionPK,
                districtPK: selectedDistrictPK,
                locationType: LocationType.Region,
                locationPK: selectedRegionPK
            };
        } else if (selectedMakePK !== ALL && selectedMakePK !== SELECT_LOCATION) {
            return {
                makePK: selectedMakePK,
                regionPK: selectedRegionPK,
                districtPK: selectedDistrictPK,
                locationType: LocationType.Make,
                locationPK: selectedMakePK
            };
        }

        return null;
    };

    return (
        <div className="row align-items-center">
            <div className="col-12 col-md-12">
                <div className="filters-section">
                    <div className="filters d-flex justify-content-end">
                        {/* Make dropdown */}
                        <select
                            key={`make-${renderResetKey}`}
                            className="form-control mx-1"
                            value={String(selectedMakePK)}
                            onChange={(e) => handleMakeSelected(e.target.value)}
                            autoComplete="off"
                        >
                            <option key={`make_${SELECT_LOCATION}`} value={String(SELECT_LOCATION)}>{pageText?.selectMakeText ?? 'Select Make'}</option>
                            {makes.map((make) => (
                                <option key={`make_${make.makePK}`} value={String(make.makePK)}>
                                    {make.title}
                                </option>
                            ))}
                        </select>

                        {/* Region dropdown */}
                        <select
                            key={`region-${renderResetKey}`}
                            className="form-control mx-1"
                            value={String(selectedRegionPK)}
                            onChange={(e) => handleRegionSelected(e.target.value)}
                            disabled={selectedMakePK === SELECT_LOCATION}
                            autoComplete="off"
                        >
                            <option key={`region_${SELECT_LOCATION}`} value={String(SELECT_LOCATION)}>{pageText?.selectRegionText ?? 'Select Region'}</option>
                            {regions.map((region) => (
                                <option key={`region_${region.regionPK}`} value={String(region.regionPK)}>
                                    {region.description}
                                </option>
                            ))}
                        </select>

                        {/* District dropdown */}
                        <select
                            key={`district-${renderResetKey}`}
                            className="form-control mx-1"
                            value={String(selectedDistrictPK)}
                            onChange={(e) => handleDistrictSelected(e.target.value)}
                            disabled={selectedMakePK === SELECT_LOCATION || selectedRegionPK === ALL || selectedRegionPK === SELECT_LOCATION}
                            autoComplete="off"
                        >
                            <option key={`district_${SELECT_LOCATION}`} value={String(SELECT_LOCATION)}>{pageText?.selectDistrictText ?? 'Select District'}</option>
                            {districts.map((district) => (
                                <option key={`district_${district.districtPK}`} value={String(district.districtPK)}>
                                    {district.description}
                                </option>
                            ))}
                        </select>

                        <button
                            className="btn btn-primary ml-2"
                            onClick={() => onSearchButtonClicked(getSelectedLocation())}
                            disabled={selectedMakePK === SELECT_LOCATION || selectedRegionPK === SELECT_LOCATION || selectedDistrictPK === SELECT_LOCATION}
                            type="button"
                        >
                            {pageText.getReportButtonLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationFilters;
